import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { ConfirmationService } from 'primeng/api';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { AuthStateService } from 'projects/admin/src/app/services/auth-state.service';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { AcModel } from 'projects/core/src/lib/models/ac.model';
import { ExerciseModel } from 'projects/core/src/lib/models/exercise.model';
import { ExerciseversionModel } from 'projects/core/src/lib/models/exerciseversion.model';
import { TemplateModel } from 'projects/core/src/lib/models/template.model';

@Component({
  selector: 'app-ac-exercises',
  templateUrl: './ac-exercises.component.html',
  styleUrls: ['./ac-exercises.component.scss']
})
export class AcExercisesComponent implements OnInit {

  @Input() ac: AcModel | TemplateModel = null;
  @Input() mode = 'ac';
  @Output() acChange = new EventEmitter<AcModel | TemplateModel>();
  @Output() setEdit = new EventEmitter<string>();

  public inEdit = false;
  public all: Array<ExerciseModel> = [];
  public exercises: Array<any> = [];
  public submitting = false;
  public form: UntypedFormGroup;
  public optionsExercises: Array<any> = [];

  public deleteInheritVisible = false;
  public deleteInheritExercise: ExerciseModel = null;
  public saveInheritVisible = false;

  constructor(
    private api: ApiService,
    private fb: UntypedFormBuilder,
    private ui: UiService,
    private confirmation: ConfirmationService,
    private transloco: TranslocoService,
    private auth: AuthStateService
  ) { }

  ngOnInit(): void {
    this.form = this.fb.group({
      exercise: new UntypedFormControl(null, [Validators.required]),
    });
    this.api.getModels('exercises', { type: this.ac.type }).subscribe({
      next: (result: any) => {
        this.all = [];
        if (result !== null && result.data.length > 0) {
          result.data.forEach((exercise: any) => {
            exercise.name = exercise.name + ' (' + exercise.language + ')';
            this.all.push(new ExerciseModel().deserialize(exercise));
          });
        }
      }
    });
  }

  onNew(): void {
    if (this.auth.hasRight('admin.ac.edit')) {
      this.startEdit();
    }
  }

  onCancel(): void {
    this.stopEdit();
    this.ui.hideMessage();
  }

  onDelete(exercise: ExerciseModel): void {
    if (this.auth.hasRight('admin.' + this.mode + '.edit')) {
      this.confirmation.confirm({
        message: this.transloco.translate('common.messages.really_delete', { value: exercise.name }),
        header: this.transloco.translate('common.really_delete'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.deleteInheritExercise = exercise;
          if (this.ac?.shouldShowInheritDialog(this.mode)) {
            this.deleteInheritVisible = true;
          } else {
            this.deleteExercise(false);
          }
        }
      });
    }
  }

  onSubmit(): void {
    if (this.ac !== null && this.auth.hasRight('admin.' + this.mode + '.edit')) {
      this.ui.hideMessage();
      if (this.form.valid === true) {
        if (this.ac?.shouldShowInheritDialog(this.mode)) {
          this.saveInheritVisible = true
        } else {
          this.saveExercise(false);
        }
      } else {
        this.ui.showMessage('error', this.transloco.translate('common.error_on_form_please_correct'), this.transloco.translate('common.error'));
        FormHelper.markFormAsDirty(this.form);
      }
    }
  }

  private deleteExercise(inherit: boolean = false): void {
    this.api.deleteAcExercise(this.mode + 's', this.ac.id, this.deleteInheritExercise?.id, inherit).subscribe({
      next: (result: any) => {
        if (result.data) {
          this.ac = new AcModel().deserialize(result.data);
          this.acChange.emit(this.ac);
          this.ui.toastSuccess(this.transloco.translate('common.messages.deleted', { value: this.deleteInheritExercise?.name }), this.transloco.translate('common.deleted'));
        }
      },
      error: (error: any) => {
        this.ui.toastError(this.transloco.translate(error.message));
      }
    });
  }

  private saveExercise(inherit: boolean = false): void {
    this.submitting = true;
    const payload = {
      exercise: this.form.get('exercise').value.id,
      inherit
    };
    this.api.saveAcExercise(this.mode + 's', this.ac.id, payload).subscribe({
      next: (result: any) => {
        if (result.data) {
          this.ac = new AcModel().deserialize(result.data);
          this.acChange.emit(this.ac);
          this.ui.toastSuccess(this.transloco.translate('ac_exercises.exercise_added'), this.transloco.translate('common.added'));
          this.stopEdit();
        }
      },
      error: (error: any) => {
        this.ui.showMessage('error', this.transloco.translate(error.message), this.transloco.translate('common.error'));
      },
    }).add(() => {
      this.submitting = false;
    });
  }

  startEdit(): void {
    this.inEdit = true;
    this.setupForm();
    this.setEdit.emit('exercises');
    // a11y focus
    this.ui.setFormFocus();
  }

  stopEdit(): void {
    this.inEdit = false;
    this.clearForm();
    this.setEdit.emit(null);
    // a11y focus
    this.ui.setFocus();
  }

  private setupForm(): void {
    this.clearForm();
    this.all.forEach((exercise: ExerciseModel) => {
      if (this.ac.exerciseversions.find((actual: ExerciseversionModel) => actual.exercise.id === exercise.id) === undefined) {
        this.optionsExercises.push(exercise);
      }
    });
  }

  private clearForm(): void {
    this.form.reset();
    this.optionsExercises = [];
    this.form.patchValue({
      exercise: null,
    });
  }
}
